import { supabase } from '@/lib/supabase/client';
import { buildSlaAgreementDraftPayload } from '@/lib/sla/draft-payload';
import {
  buildSlaAgreementNumber,
  DEFAULT_SLA_TEMPLATE_KEY,
  DEFAULT_SLA_TEMPLATE_VERSION,
  normalizeSlaAgreement,
  normalizeSlaAgreementEvent,
  normalizeSlaAgreementSendPackage,
  normalizeSlaDraftPayload,
  normalizeSlaReconciliation,
  normalizeSlaWritebackResult,
} from '@/lib/sla/normalize';
import type {
  SlaAgreement,
  SlaAgreementEvent,
  SlaAgreementInsert,
  SlaAgreementSendPackage,
  SlaAgreementUpdate,
} from '@/types/sla-agreements';
import { contactsService } from './contacts';
import { dealsService } from './deals';
import { locationsService } from './locations';
import { quotesService } from './quotes';

interface ActiveAgreementSourceLookup {
  quote_id?: string | null;
  invoice_id?: string | null;
  deal_id?: string | null;
  primary_location_id?: string | null;
}

async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

async function getNextAgreementNumber(): Promise<string> {
  const { data, error } = await supabase.from('sla_agreements').select('agreement_number');

  if (error) {
    console.error('Error loading agreement numbers:', error);
    throw new Error(`Failed to generate agreement number: ${error.message}`);
  }

  const numbers = (data || []).map((row) => String(row.agreement_number));
  return buildSlaAgreementNumber(numbers);
}

async function resolveQuoteLocationId(input: {
  locationId: string | null;
  dealId: string | null;
}): Promise<string | null> {
  if (input.locationId) {
    return input.locationId;
  }

  if (!input.dealId) {
    return null;
  }

  const deal = await dealsService.getById(input.dealId);
  return deal?.location_id || null;
}

export const slaAgreementsService = {
  async getAll(): Promise<SlaAgreement[]> {
    const { data, error } = await supabase
      .from('sla_agreements')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching SLA agreements:', error);
      return [];
    }

    return ((data as Record<string, unknown>[]) || []).map((row) => normalizeSlaAgreement(row));
  },

  async getById(id: string): Promise<SlaAgreement | null> {
    const { data, error } = await supabase.from('sla_agreements').select('*').eq('id', id).single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching SLA agreement:', error);
      return null;
    }

    return data ? normalizeSlaAgreement(data as Record<string, unknown>) : null;
  },

  async getByQuoteId(quoteId: string): Promise<SlaAgreement[]> {
    const { data, error } = await supabase
      .from('sla_agreements')
      .select('*')
      .eq('quote_id', quoteId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching SLA agreements by quote:', error);
      return [];
    }

    return ((data as Record<string, unknown>[]) || []).map((row) => normalizeSlaAgreement(row));
  },

  async getActiveAgreementForSource(input: ActiveAgreementSourceLookup): Promise<SlaAgreement | null> {
    let query = supabase
      .from('sla_agreements')
      .select('*')
      .neq('status', 'voided')
      .neq('status', 'superseded')
      .order('created_at', { ascending: false })
      .limit(1);

    if (input.quote_id) {
      query = query.eq('quote_id', input.quote_id);
    } else if (input.invoice_id) {
      query = query.eq('invoice_id', input.invoice_id);
    } else if (input.deal_id) {
      query = query.eq('deal_id', input.deal_id);
    } else if (input.primary_location_id) {
      query = query.eq('primary_location_id', input.primary_location_id);
    } else {
      return null;
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching active SLA agreement by source:', error);
      return null;
    }

    const row = (data as Record<string, unknown>[] | null)?.[0];
    return row ? normalizeSlaAgreement(row) : null;
  },

  async create(input: SlaAgreementInsert): Promise<SlaAgreement> {
    const userId = await getCurrentUserId();
    const sourceLookup: ActiveAgreementSourceLookup = {
      quote_id: input.quote_id ?? null,
      invoice_id: input.invoice_id ?? null,
      deal_id: input.deal_id ?? null,
      primary_location_id: input.primary_location_id ?? null,
    };

    const existingActiveAgreement = await this.getActiveAgreementForSource(sourceLookup);
    if (existingActiveAgreement) {
      return existingActiveAgreement;
    }

    const insertData: Record<string, unknown> = {
      agreement_type: input.agreement_type || 'master_agreement',
      status: input.status || 'draft',
      template_key: input.template_key || DEFAULT_SLA_TEMPLATE_KEY,
      template_version: input.template_version || DEFAULT_SLA_TEMPLATE_VERSION,
      quote_id: input.quote_id ?? null,
      invoice_id: input.invoice_id ?? null,
      deal_id: input.deal_id ?? null,
      primary_location_id: input.primary_location_id ?? null,
      draft_payload_json: normalizeSlaDraftPayload(input.draft_payload_json),
      reconciliation_json: normalizeSlaReconciliation(input.reconciliation_json),
      writeback_result_json: normalizeSlaWritebackResult(input.writeback_result_json),
      current_send_package_id: input.current_send_package_id ?? null,
      generated_file_path: input.generated_file_path ?? null,
      signed_file_path: input.signed_file_path ?? null,
      finalized_file_path: input.finalized_file_path ?? null,
      generated_at: input.generated_at ?? null,
      signature_requested_at: input.signature_requested_at ?? null,
      signature_request_expires_at: input.signature_request_expires_at ?? null,
      signature_request_revoked_at: input.signature_request_revoked_at ?? null,
      signature_request_email_id: input.signature_request_email_id ?? null,
      sent_at: input.sent_at ?? null,
      signed_uploaded_at: input.signed_uploaded_at ?? null,
      finalized_at: input.finalized_at ?? null,
      completed_at: input.completed_at ?? null,
      completed_delivery_at: input.completed_delivery_at ?? null,
      completed_delivery_email_id: input.completed_delivery_email_id ?? null,
      voided_at: input.voided_at ?? null,
      created_by: userId,
      updated_by: userId,
    };

    if (input.agreement_number) {
      insertData.agreement_number = input.agreement_number;
    }

    const attemptInsert = async (payload: Record<string, unknown>) =>
      supabase.from('sla_agreements').insert(payload).select().single();

    let { data, error } = await attemptInsert(insertData);

    if (
      error &&
      !input.agreement_number &&
      (error.message?.includes('agreement_number') || error.message?.includes('null value in column'))
    ) {
      const fallbackAgreementNumber = await getNextAgreementNumber();
      ({ data, error } = await attemptInsert({
        ...insertData,
        agreement_number: fallbackAgreementNumber,
      }));
    }

    if (error) {
      if (error.code === '23505') {
        const sourceConflictAgreement = await this.getActiveAgreementForSource(sourceLookup);
        if (sourceConflictAgreement) {
          return sourceConflictAgreement;
        }
      }
      console.error('Error creating SLA agreement:', error);
      throw new Error(`Failed to create SLA agreement: ${error.message}`);
    }

    return normalizeSlaAgreement(data as Record<string, unknown>);
  },

  async update(id: string, input: SlaAgreementUpdate): Promise<SlaAgreement> {
    const userId = await getCurrentUserId();
    const updateData: Record<string, unknown> = {
      updated_by: userId,
    };

    if (input.agreement_number !== undefined) updateData.agreement_number = input.agreement_number;
    if (input.agreement_type !== undefined) updateData.agreement_type = input.agreement_type;
    if (input.status !== undefined) updateData.status = input.status;
    if (input.template_key !== undefined) updateData.template_key = input.template_key;
    if (input.template_version !== undefined) updateData.template_version = input.template_version;
    if (input.quote_id !== undefined) updateData.quote_id = input.quote_id;
    if (input.invoice_id !== undefined) updateData.invoice_id = input.invoice_id;
    if (input.deal_id !== undefined) updateData.deal_id = input.deal_id;
    if (input.primary_location_id !== undefined) updateData.primary_location_id = input.primary_location_id;
    if (input.draft_payload_json !== undefined) {
      updateData.draft_payload_json = normalizeSlaDraftPayload(input.draft_payload_json);
    }
    if (input.reconciliation_json !== undefined) {
      updateData.reconciliation_json = normalizeSlaReconciliation(input.reconciliation_json);
    }
    if (input.writeback_result_json !== undefined) {
      updateData.writeback_result_json = normalizeSlaWritebackResult(input.writeback_result_json);
    }
    if (input.current_send_package_id !== undefined) updateData.current_send_package_id = input.current_send_package_id;
    if (input.generated_file_path !== undefined) updateData.generated_file_path = input.generated_file_path;
    if (input.signed_file_path !== undefined) updateData.signed_file_path = input.signed_file_path;
    if (input.finalized_file_path !== undefined) updateData.finalized_file_path = input.finalized_file_path;
    if (input.generated_at !== undefined) updateData.generated_at = input.generated_at;
    if (input.signature_requested_at !== undefined) {
      updateData.signature_requested_at = input.signature_requested_at;
    }
    if (input.signature_request_expires_at !== undefined) {
      updateData.signature_request_expires_at = input.signature_request_expires_at;
    }
    if (input.signature_request_revoked_at !== undefined) {
      updateData.signature_request_revoked_at = input.signature_request_revoked_at;
    }
    if (input.signature_request_email_id !== undefined) {
      updateData.signature_request_email_id = input.signature_request_email_id;
    }
    if (input.sent_at !== undefined) updateData.sent_at = input.sent_at;
    if (input.signed_uploaded_at !== undefined) updateData.signed_uploaded_at = input.signed_uploaded_at;
    if (input.finalized_at !== undefined) updateData.finalized_at = input.finalized_at;
    if (input.completed_at !== undefined) updateData.completed_at = input.completed_at;
    if (input.completed_delivery_at !== undefined) updateData.completed_delivery_at = input.completed_delivery_at;
    if (input.completed_delivery_email_id !== undefined) {
      updateData.completed_delivery_email_id = input.completed_delivery_email_id;
    }
    if (input.voided_at !== undefined) updateData.voided_at = input.voided_at;

    const { data, error } = await supabase
      .from('sla_agreements')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating SLA agreement:', error);
      throw new Error(`Failed to update SLA agreement: ${error.message}`);
    }

    return normalizeSlaAgreement(data as Record<string, unknown>);
  },

  async markSignatureRequested(
    id: string,
    input: {
      current_send_package_id: string;
      signature_requested_at: string;
      signature_request_expires_at: string | null;
      signature_request_email_id: string | null;
    }
  ): Promise<SlaAgreement> {
    return this.update(id, {
      status: 'signature_requested',
      current_send_package_id: input.current_send_package_id,
      signature_requested_at: input.signature_requested_at,
      signature_request_expires_at: input.signature_request_expires_at,
      signature_request_revoked_at: null,
      signature_request_email_id: input.signature_request_email_id,
    });
  },

  async markSignatureExpired(id: string, expiredAt: string): Promise<SlaAgreement> {
    return this.update(id, {
      status: 'expired',
      signature_request_expires_at: expiredAt,
    });
  },

  async markSignatureRevoked(id: string, revokedAt: string): Promise<SlaAgreement> {
    return this.update(id, {
      status: 'generated',
      current_send_package_id: null,
      signature_request_revoked_at: revokedAt,
      signature_request_expires_at: null,
      signature_request_email_id: null,
    });
  },

  async markSuperseded(id: string): Promise<SlaAgreement> {
    return this.update(id, {
      status: 'superseded',
      current_send_package_id: null,
      signature_request_expires_at: null,
      signature_request_email_id: null,
    });
  },

  async markAwaitingTpbSignature(id: string): Promise<SlaAgreement> {
    return this.update(id, {
      status: 'awaiting_tpb_signature',
    });
  },

  async markCompletedFromEsign(
    id: string,
    input: {
      finalized_file_path: string;
      finalized_at: string;
      writeback_result_json: SlaAgreement['writeback_result_json'];
    }
  ): Promise<SlaAgreement> {
    return this.update(id, {
      status: 'completed',
      finalized_file_path: input.finalized_file_path,
      signed_file_path: input.finalized_file_path,
      finalized_at: input.finalized_at,
      writeback_result_json: input.writeback_result_json,
      completed_at: input.finalized_at,
    });
  },

  async markCompletedDelivery(
    id: string,
    input: {
      completed_delivery_at: string;
      completed_delivery_email_id: string | null;
    }
  ): Promise<SlaAgreement> {
    return this.update(id, {
      completed_delivery_at: input.completed_delivery_at,
      completed_delivery_email_id: input.completed_delivery_email_id,
    });
  },

  async getCurrentSendPackage(agreementId: string): Promise<SlaAgreementSendPackage | null> {
    const agreement = await this.getById(agreementId);
    if (!agreement?.current_send_package_id) {
      return null;
    }

    const { data, error } = await supabase
      .from('sla_agreement_send_packages')
      .select(`
        *,
        signers:sla_agreement_signers (
          *,
          tokens:sla_agreement_signer_tokens (*)
        )
      `)
      .eq('id', agreement.current_send_package_id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching current SLA send package:', error);
      throw new Error(`Failed to fetch current send package: ${error.message}`);
    }

    return normalizeSlaAgreementSendPackage(data as Record<string, unknown>);
  },

  async getAgreementTimeline(agreementId: string): Promise<SlaAgreementEvent[]> {
    const { data, error } = await supabase
      .from('sla_agreement_events')
      .select('*')
      .eq('agreement_id', agreementId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching SLA agreement timeline:', error);
      throw new Error(`Failed to fetch agreement timeline: ${error.message}`);
    }

    return ((data as Record<string, unknown>[]) || []).map((row) => normalizeSlaAgreementEvent(row));
  },

  async createFromQuote(quoteId: string): Promise<SlaAgreement> {
    const activeAgreement = await this.getActiveAgreementForSource({ quote_id: quoteId });
    if (activeAgreement) {
      return activeAgreement;
    }

    const quote = await quotesService.getById(quoteId);
    if (!quote) {
      throw new Error('Quote not found');
    }

    if (quote.status !== 'accepted') {
      throw new Error('Only accepted quotes can create SLA agreements');
    }

    const locationId = await resolveQuoteLocationId({
      locationId: quote.location_id,
      dealId: quote.deal_id,
    });
    const location = locationId ? await locationsService.getById(locationId) : null;

    const deal = quote.deal_id ? await dealsService.getById(quote.deal_id) : null;
    const items = await quotesService.getItemsByQuoteId(quote.id);
    const sourceContact = quote.contact_id ? await contactsService.getById(quote.contact_id) : null;
    const dealContact = deal?.contact_id ? await contactsService.getById(deal.contact_id) : null;
    const locationContacts = location?.id ? await contactsService.getByLocationId(location.id) : [];

    return this.create({
      quote_id: quote.id,
      deal_id: quote.deal_id,
      primary_location_id: location?.id ?? null,
      draft_payload_json: buildSlaAgreementDraftPayload({
        source_type: 'quote',
        source_reference: quote.quote_number,
        source_quote_number: quote.quote_number,
        customer_name: quote.customer_name || deal?.name || location?.location_name || null,
        agreement_terms: quote.agreement_terms,
        deal,
        location,
        source_contact: sourceContact,
        deal_contact: dealContact,
        location_contacts: locationContacts,
        items,
      }),
    });
  },
};
